import React, { useEffect, useState } from "react";
import { CreditCard, Spinner, PaperPlaneTilt, CheckCircle, Clock, ArrowSquareOut, Copy } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

function StatusBadge({ status, testid }) {
  const map = {
    pending: { cls: "border-amber-500/40 text-amber-300 bg-amber-500/10", icon: Clock, label: "PENDING" },
    paid: { cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10", icon: CheckCircle, label: "PAID" },
    expired: { cls: "border-zinc-700 text-zinc-400 bg-zinc-900", icon: Clock, label: "EXPIRED" },
  };
  const m = map[status] || map.pending;
  const Icon = m.icon;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider ${m.cls}`}>
      <Icon size={10} weight="fill" /> {m.label}
    </span>
  );
}

export default function ProposalsTab({ lead, stripeConfigured, onLeadUpdated }) {
  const [packages, setPackages] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [packageId, setPackageId] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [customDesc, setCustomDesc] = useState("");
  const [mode, setMode] = useState("package");
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState({});

  useEffect(() => {
    if (!stripeConfigured) return;
    api.get("/payments/packages").then((r) => {
      setPackages(r.data);
      if (r.data?.length && !packageId) setPackageId(r.data[0].id);
    }).catch(() => {});
  }, [stripeConfigured]); // eslint-disable-line

  const loadProposals = async () => {
    if (!lead?.id) return;
    try {
      const { data } = await api.get(`/payments/proposals/${lead.id}`);
      setProposals(data);
    } catch {
      // silent
    }
  };

  useEffect(() => { loadProposals(); }, [lead?.id]);

  const create = async () => {
    if (!stripeConfigured) { toast.error("Stripe not configured"); return; }
    setCreating(true);
    try {
      const body = {
        lead_id: lead.id,
        origin_url: window.location.origin,
        advance_stage: true,
      };
      if (mode === "package") {
        if (!packageId) { toast.error("Pick a package"); setCreating(false); return; }
        body.package_id = packageId;
      } else {
        const amt = parseFloat(customAmount);
        if (!amt || amt <= 0) { toast.error("Enter a valid amount"); setCreating(false); return; }
        body.custom_amount_usd = amt;
        body.custom_title = customTitle || "Custom Proposal";
        body.custom_description = customDesc;
      }
      const { data } = await api.post("/payments/proposal", body);
      toast.success("Stripe checkout created");
      setProposals((p) => [data.proposal, ...p]);
      // Notify parent lead got status updated
      if (onLeadUpdated) onLeadUpdated({ ...lead, status: "Proposal Sent" });
      // Reset form
      setCustomAmount(""); setCustomTitle(""); setCustomDesc("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${label}`);
  };

  const refresh = async (p) => {
    setRefreshing((r) => ({ ...r, [p.id]: true }));
    try {
      const { data } = await api.get(`/payments/checkout/status/${p.session_id}`);
      setProposals((ps) => ps.map((x) => (x.id === data.id ? data : x)));
      if (data.payment_status === "paid" && onLeadUpdated) {
        onLeadUpdated({ ...lead, status: "Won" });
      }
      toast.success(`Status: ${data.payment_status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Refresh failed");
    } finally {
      setRefreshing((r) => ({ ...r, [p.id]: false }));
    }
  };

  return (
    <div className="space-y-4">
      <div className={`bg-zinc-900/60 border border-zinc-800 rounded-sm p-4 ${creating ? "ai-beam" : ""}`}>
        <div className="flex items-center gap-2 mb-3">
          <CreditCard size={16} weight="duotone" className="text-orange-500" />
          <div className="font-display font-medium text-zinc-100">Send a paid proposal</div>
        </div>

        {!stripeConfigured && (
          <div className="text-xs text-amber-400 bg-amber-500/5 border border-amber-500/20 rounded-sm px-3 py-2 mb-3" data-testid="stripe-not-configured">
            Stripe is not configured. Add STRIPE_API_KEY to backend/.env to enable real checkout.
          </div>
        )}

        <div className="flex gap-2 mb-3">
          <Button size="sm" variant={mode === "package" ? "default" : "outline"} data-testid="proposal-mode-package" onClick={() => setMode("package")} className={mode === "package" ? "bg-orange-600 hover:bg-orange-500 text-zinc-950" : "border-zinc-800"}>Preset package</Button>
          <Button size="sm" variant={mode === "custom" ? "default" : "outline"} data-testid="proposal-mode-custom" onClick={() => setMode("custom")} className={mode === "custom" ? "bg-orange-600 hover:bg-orange-500 text-zinc-950" : "border-zinc-800"}>Custom amount</Button>
        </div>

        {mode === "package" ? (
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Package</Label>
            <Select value={packageId} onValueChange={setPackageId}>
              <SelectTrigger data-testid="proposal-package-select" className="mt-1.5 bg-zinc-950 border-zinc-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {packages.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} — ${p.amount_usd.toFixed(2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {packages.find((p) => p.id === packageId)?.description && (
              <div className="text-xs text-zinc-500 mt-1.5">{packages.find((p) => p.id === packageId).description}</div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Amount USD</Label>
              <Input data-testid="proposal-custom-amount" type="number" min={1} step={0.01} value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} className="mt-1.5 bg-zinc-950 border-zinc-800" placeholder="500.00" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Title</Label>
              <Input data-testid="proposal-custom-title" value={customTitle} onChange={(e) => setCustomTitle(e.target.value)} className="mt-1.5 bg-zinc-950 border-zinc-800" placeholder="Website Build Deposit" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Description</Label>
              <Textarea data-testid="proposal-custom-desc" rows={2} value={customDesc} onChange={(e) => setCustomDesc(e.target.value)} className="mt-1.5 bg-zinc-950 border-zinc-800" />
            </div>
          </div>
        )}

        <Button
          data-testid="proposal-create-btn"
          onClick={create}
          disabled={creating || !stripeConfigured}
          className="mt-4 w-full bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
        >
          {creating ? (<><Spinner size={14} className="mr-1.5 animate-spin" /> Creating session…</>) : (<><PaperPlaneTilt size={14} className="mr-1.5" weight="fill" /> Generate Stripe Checkout Link</>)}
        </Button>
      </div>

      <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm">
        <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center justify-between">
          <div className="font-display font-medium text-zinc-100 text-sm">Proposals & Payments</div>
          <div className="font-mono text-[10px] text-zinc-500" data-testid="proposals-count">{proposals.length}</div>
        </div>
        {proposals.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-zinc-500" data-testid="proposals-empty">
            No proposals sent to this lead yet.
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/60">
            {proposals.map((p) => (
              <div key={p.id} data-testid={`proposal-row-${p.id}`} className="px-4 py-3">
                <div className="flex items-center justify-between gap-3 mb-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-100 font-medium truncate">{p.title}</div>
                    <div className="font-mono text-[11px] text-zinc-500">
                      ${p.amount_usd.toFixed(2)} {p.currency?.toUpperCase()} · {new Date(p.created_at).toLocaleString()}
                    </div>
                  </div>
                  <StatusBadge status={p.status} testid={`proposal-status-${p.id}`} />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <Button size="sm" variant="outline" data-testid={`proposal-open-${p.id}`} onClick={() => window.open(p.checkout_url, "_blank")} className="h-7 border-zinc-800 text-zinc-300 hover:bg-zinc-900 rounded-sm">
                    <ArrowSquareOut size={12} className="mr-1" /> Open
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`proposal-copy-${p.id}`} onClick={() => copy(p.checkout_url, "checkout URL")} className="h-7 text-zinc-400 hover:text-zinc-100">
                    <Copy size={12} className="mr-1" /> Copy link
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`proposal-refresh-${p.id}`} onClick={() => refresh(p)} disabled={refreshing[p.id]} className="h-7 text-orange-400 hover:text-orange-300">
                    {refreshing[p.id] ? <Spinner size={12} className="animate-spin mr-1" /> : <Clock size={12} className="mr-1" />}
                    Refresh status
                  </Button>
                </div>
                {p.paid_at && (
                  <div className="mt-2 px-2 py-1 bg-emerald-500/5 border border-emerald-500/20 rounded-sm text-[10px] font-mono text-emerald-400" data-testid={`proposal-paid-${p.id}`}>
                    ✓ Paid {new Date(p.paid_at).toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
